import { action, Action, thunk, Thunk } from 'easy-peasy'
import {
  Routes,
  SimpleV2ReasonCode,
  SIMPLE_V2_REASON_CODE,
} from '@mortgage-pos/types'
import { simpleV2TrackingService } from '@mortgage-pos/shared-frontend'
import incense from '@mortgage-pos/ui/incense'
import { simpleV2OfframpRows } from '@mortgage-pos/ui/data/simple/simpleV2OfframpConfig'
import type {
  SimpleV2OfframpRowKey,
  SimpleV2OfframpStatus,
  SimpleV2OfframpStatusItem,
} from '@mortgage-pos/ui/data/simple/simpleV2OfframpConfig'
import { redirectToOtherQuestionSet } from '../utils/navigation'
import {
  buildAlfiePrePricingSlug,
  persistOffRampReason,
} from '../utils/offrampHelpers'
import type { StoreModel } from './index'
import { suppressAbandonmentOfframp } from '../utils/abandonmentOfframpSuppress'

export type OffRampEvaluation =
  | { outcome: 'continue' }
  | {
      outcome: 'offramp'
      variant: 'soft' | 'hard'
      reasonCode: SimpleV2ReasonCode
    }

interface SimpleV2OfframpStatusSummary {
  completedItems: SimpleV2OfframpStatusItem[]
  attentionItems: SimpleV2OfframpStatusItem[]
}

export const OFFRAMP_ROUTES = {
  soft: `/${Routes.APP_1003}/simple-v2-offramp`,
  hardFailed: `/${Routes.APP_1003}/simple-v2-offramp-failed`,
} as const

export const OFFRAMP_PATHS: readonly string[] = [
  OFFRAMP_ROUTES.soft,
  OFFRAMP_ROUTES.hardFailed,
]

type EvalThunk = Thunk<
  OfframpModel,
  void,
  object,
  StoreModel,
  Promise<OffRampEvaluation>
>

export interface OfframpModel {
  softOfframpReasonCode: SimpleV2ReasonCode | null
  // TODO: Consolidate this with softOfframpReasonCode into one offrampReasonCode
  // once hard and soft off-ramp paths share the same state semantics.
  lastOfframpReasonCode: SimpleV2ReasonCode | null
  hasCoBorrowerEvaluated: boolean
  statusSummary: SimpleV2OfframpStatusSummary
  hasOfframped: boolean

  setSoftOfframpReasonCode: Action<OfframpModel, SimpleV2ReasonCode | null>
  setLastOfframpReasonCode: Action<OfframpModel, SimpleV2ReasonCode | null>
  setHasCoBorrowerEvaluated: Action<OfframpModel, boolean>
  setStatusSummary: Action<OfframpModel, SimpleV2OfframpStatusSummary>
  buildStatusSummary: Thunk<OfframpModel, void, object, StoreModel>
  setHasOfframped: Action<OfframpModel>

  offRampToAlfiePrePricing: Thunk<
    OfframpModel,
    { reasonCode: SimpleV2ReasonCode },
    object,
    StoreModel
  >

  evaluateCoBorrowerStep: Thunk<OfframpModel, void, object, StoreModel>

  evaluatePlaidAnalysisStep: EvalThunk
  evaluateCreditStep: EvalThunk
  evaluateAusStep: EvalThunk
}

function softOfframp(
  applicationGuid: string | null,
  reasonCode: SimpleV2ReasonCode,
  page: string,
  stepNumber: number
): OffRampEvaluation {
  if (applicationGuid) persistOffRampReason(applicationGuid, reasonCode)
  simpleV2TrackingService.simpleV2Offramp(reasonCode, page, stepNumber)
  return { outcome: 'offramp', variant: 'soft', reasonCode }
}

function hardOfframp(
  applicationGuid: string | null,
  reasonCode: SimpleV2ReasonCode,
  page: string,
  stepNumber: number
): OffRampEvaluation {
  if (applicationGuid) persistOffRampReason(applicationGuid, reasonCode)
  simpleV2TrackingService.simpleV2Offramp(reasonCode, page, stepNumber)
  return { outcome: 'offramp', variant: 'hard', reasonCode }
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

function hasKnownAddress(answers: Record<string, unknown>): boolean {
  const directAddress =
    hasValue(answers.street) &&
    hasValue(answers.city) &&
    hasValue(answers.state) &&
    hasValue(answers.zipCode)

  const addressGroups = [answers['addresses+0'], answers.addresses].filter(
    Array.isArray
  ) as Array<Array<Record<string, unknown>>>

  const groupedAddress = addressGroups.some((group) =>
    group.some(
      (address) =>
        address?.type === 'current' &&
        hasValue(address.street) &&
        hasValue(address.zipCode)
    )
  )

  return directAddress || groupedAddress
}

function buildStatusSummaryFromStatuses(
  statuses: Record<SimpleV2OfframpRowKey, SimpleV2OfframpStatus>
): SimpleV2OfframpStatusSummary {
  return (
    Object.keys(statuses) as SimpleV2OfframpRowKey[]
  ).reduce<SimpleV2OfframpStatusSummary>(
    (summary, key) => {
      const status = statuses[key]
      const item = simpleV2OfframpRows[key][status]
      if (!item) return summary

      if (status === 'complete') {
        summary.completedItems.push(item)
        return summary
      }

      summary.attentionItems.push(item)
      return summary
    },
    { completedItems: [], attentionItems: [] }
  )
}

const offramp = (): OfframpModel => ({
  softOfframpReasonCode: null,
  lastOfframpReasonCode: null,
  hasCoBorrowerEvaluated: false,
  statusSummary: { completedItems: [], attentionItems: [] },
  hasOfframped: false,

  setSoftOfframpReasonCode: action((state, payload) => {
    state.softOfframpReasonCode = payload
  }),

  setLastOfframpReasonCode: action((state, payload) => {
    state.lastOfframpReasonCode = payload
  }),

  setHasCoBorrowerEvaluated: action((state, payload) => {
    state.hasCoBorrowerEvaluated = payload
  }),

  setStatusSummary: action((state, payload) => {
    state.statusSummary = payload
  }),

  buildStatusSummary: thunk((actions, _, { getState, getStoreState }) => {
    const { lastOfframpReasonCode } = getState()
    const storeState = getStoreState()
    const { mergedAnswers } = storeState.answers
    const { accounts, incomeAnalysis, incomeAnalysisError } = storeState.plaid
    const { hasOrderedSimpleV2Credit, orderCreditError } = storeState.credit
    const { hasOrderedUnderwrite, hasUsableAusReport, isAusIneligible } =
      storeState.fannie
    const { rates } = storeState.rates

    const personalDetailsComplete = [
      mergedAnswers.firstName,
      mergedAnswers.lastName,
      mergedAnswers.email,
      mergedAnswers.phone,
    ].some(hasValue)

    const plaidEvaluationReached =
      Boolean(incomeAnalysis) || Boolean(incomeAnalysisError)
    const bankAccountsFailedReasons: SimpleV2ReasonCode[] = [
      SIMPLE_V2_REASON_CODE.plaidFail,
      SIMPLE_V2_REASON_CODE.plaidAbandon,
      SIMPLE_V2_REASON_CODE.plaidOptOut,
    ]
    const bankAccountsFailedReason =
      lastOfframpReasonCode !== null &&
      bankAccountsFailedReasons.includes(lastOfframpReasonCode)

    const bankAccountsStatus: SimpleV2OfframpStatus =
      Array.isArray(accounts) && accounts.length > 0
        ? 'complete'
        : bankAccountsFailedReason ||
            (plaidEvaluationReached &&
              Array.isArray(accounts) &&
              accounts.length === 0)
          ? 'failed'
          : 'not_reached'

    const incomeGrossAnnual =
      incomeAnalysis?.data?.gross_income_estimate?.estimated_gross_annual
    const incomeFailedReasons: SimpleV2ReasonCode[] = [
      SIMPLE_V2_REASON_CODE.incomeConfidenceLow,
      SIMPLE_V2_REASON_CODE.plaidFail,
    ]
    const incomeFailedReason =
      lastOfframpReasonCode !== null &&
      incomeFailedReasons.includes(lastOfframpReasonCode)
    const incomeStatus: SimpleV2OfframpStatus =
      incomeAnalysis?.status === 'completed' &&
      typeof incomeGrossAnnual === 'number' &&
      incomeGrossAnnual > 0
        ? 'complete'
        : incomeAnalysisError ||
            incomeAnalysis?.status === 'failed' ||
            incomeAnalysis?.error ||
            incomeFailedReason
          ? 'failed'
          : 'not_reached'

    const addressFailed =
      lastOfframpReasonCode === SIMPLE_V2_REASON_CODE.noAddress
    const propertyStatus: SimpleV2OfframpStatus = hasKnownAddress(mergedAnswers)
      ? 'complete'
      : addressFailed
        ? 'failed'
        : 'not_reached'

    const creditFailed =
      Boolean(orderCreditError) ||
      lastOfframpReasonCode === SIMPLE_V2_REASON_CODE.creditFrozen
    const identityCreditStatus: SimpleV2OfframpStatus =
      mergedAnswers.hasSavedSSN === true && hasOrderedSimpleV2Credit
        ? 'complete'
        : creditFailed
          ? 'failed'
          : 'not_reached'

    const automatedReviewFailedReasons: SimpleV2ReasonCode[] = [
      SIMPLE_V2_REASON_CODE.noViableProducts,
      SIMPLE_V2_REASON_CODE.dtiUnviable,
    ]
    const automatedReviewFailedReason =
      lastOfframpReasonCode !== null &&
      automatedReviewFailedReasons.includes(lastOfframpReasonCode)
    const ausFailed =
      hasOrderedUnderwrite && (!hasUsableAusReport || isAusIneligible)
    const automatedReviewStatus: SimpleV2OfframpStatus =
      (Array.isArray(rates) && rates.length > 0) ||
      (hasOrderedUnderwrite && hasUsableAusReport && !isAusIneligible)
        ? 'complete'
        : automatedReviewFailedReason || ausFailed
          ? 'failed'
          : 'not_reached'

    actions.setStatusSummary(
      buildStatusSummaryFromStatuses({
        personalDetails: personalDetailsComplete ? 'complete' : 'not_reached',
        bankAccountsAssets: bankAccountsStatus,
        incomeEmployment: incomeStatus,
        propertyHousing: propertyStatus,
        identityCredit: identityCreditStatus,
        automatedReviewOptions: automatedReviewStatus,
      })
    )
  }),

  setHasOfframped: action((state) => {
    state.hasOfframped = true
  }),

  offRampToAlfiePrePricing: thunk(
    async (actions, payload, { getStoreState }) => {
      const { applicationGuid } = getStoreState().application
      const { loanPurpose } = getStoreState().answers.mergedAnswers
      const {
        slug: questionSetSlug,
        pages,
        pageIndex,
      } = getStoreState().questionnaire

      const slug = buildAlfiePrePricingSlug(loanPurpose, questionSetSlug)
      const currentPage = pages?.[pageIndex]?.name ?? `page-${pageIndex}`

      actions.setLastOfframpReasonCode(payload.reasonCode)

      if (applicationGuid) {
        await persistOffRampReason(applicationGuid, payload.reasonCode)
      }

      actions.setHasOfframped()

      simpleV2TrackingService.simpleV2Offramp(
        payload.reasonCode,
        currentPage,
        pageIndex
      )

      incense()
        .details({
          name: 'OfframpAlfie',
          message: 'Simple v2 off-ramp to Alfie pre-pricing',
        })
        .safe({ reasonCode: payload.reasonCode, slug })
        .info()

      suppressAbandonmentOfframp()
      redirectToOtherQuestionSet(slug)
    }
  ),

  evaluateCoBorrowerStep: thunk(
    async (actions, _, { getStoreState, getState }) => {
      // Guard against double-mount (e.g. two component instances during route transition)
      if (getState().hasCoBorrowerEvaluated) return
      actions.setHasCoBorrowerEvaluated(true)

      const hasCoBorrower = getStoreState().answers.mergedAnswers?.hasCoBorrower
      if (hasCoBorrower === 'Yes') {
        await actions.offRampToAlfiePrePricing({
          reasonCode: SIMPLE_V2_REASON_CODE.multiBorrower,
        })
      }
    }
  ),

  evaluatePlaidAnalysisStep: thunk(
    async (actions, _, { getStoreState }): Promise<OffRampEvaluation> => {
      const { applicationGuid } = getStoreState().application
      const { accounts, incomeAnalysis, incomeAnalysisError } =
        getStoreState().plaid
      const { pages, pageIndex } = getStoreState().questionnaire
      const currentPage = pages?.[pageIndex]?.name ?? `page-${pageIndex}`

      if (Array.isArray(accounts) && accounts.length === 0) {
        actions.setSoftOfframpReasonCode(SIMPLE_V2_REASON_CODE.plaidFail)
        actions.setLastOfframpReasonCode(SIMPLE_V2_REASON_CODE.plaidFail)
        actions.setHasOfframped()
        return softOfframp(
          applicationGuid,
          SIMPLE_V2_REASON_CODE.plaidFail,
          currentPage,
          pageIndex
        )
      }

      const hardFailure =
        Boolean(incomeAnalysisError) ||
        incomeAnalysis?.status === 'failed' ||
        Boolean(incomeAnalysis?.error)

      if (hardFailure) {
        actions.setSoftOfframpReasonCode(SIMPLE_V2_REASON_CODE.plaidFail)
        actions.setLastOfframpReasonCode(SIMPLE_V2_REASON_CODE.plaidFail)
        actions.setHasOfframped()
        return softOfframp(
          applicationGuid,
          SIMPLE_V2_REASON_CODE.plaidFail,
          currentPage,
          pageIndex
        )
      }

      if (
        !incomeAnalysis?.data ||
        !(
          incomeAnalysis?.data?.gross_income_estimate?.estimated_gross_annual >
          0
        )
      ) {
        actions.setSoftOfframpReasonCode(
          SIMPLE_V2_REASON_CODE.incomeConfidenceLow
        )
        actions.setLastOfframpReasonCode(
          SIMPLE_V2_REASON_CODE.incomeConfidenceLow
        )
        actions.setHasOfframped()
        return softOfframp(
          applicationGuid,
          SIMPLE_V2_REASON_CODE.incomeConfidenceLow,
          currentPage,
          pageIndex
        )
      }

      return { outcome: 'continue' }
    }
  ),

  evaluateCreditStep: thunk(
    async (actions, _, { getStoreState }): Promise<OffRampEvaluation> => {
      const { orderCreditError } = getStoreState().credit
      const { applicationGuid } = getStoreState().application
      const { pages, pageIndex } = getStoreState().questionnaire
      const currentPage = pages?.[pageIndex]?.name ?? `page-${pageIndex}`

      if (orderCreditError) {
        actions.setLastOfframpReasonCode(SIMPLE_V2_REASON_CODE.creditFrozen)
        actions.setHasOfframped()
        return hardOfframp(
          applicationGuid,
          SIMPLE_V2_REASON_CODE.creditFrozen,
          currentPage,
          pageIndex
        )
      }
      return { outcome: 'continue' }
    }
  ),

  evaluateAusStep: thunk(
    async (_actions, _, { getStoreState }): Promise<OffRampEvaluation> => {
      const { hasUsableAusReport, isAusIneligible } = getStoreState().fannie
      const { applicationGuid } = getStoreState().application
      const { pages, pageIndex } = getStoreState().questionnaire
      const currentPage = pages?.[pageIndex]?.name ?? `page-${pageIndex}`

      // Temporary disable for demo
      // if (!hasUsableAusReport) {
      //   _actions.setLastOfframpReasonCode(
      //     SIMPLE_V2_REASON_CODE.noViableProducts
      //   )
      //   _actions.setHasOfframped()
      //   return hardOfframp(
      //     applicationGuid,
      //     SIMPLE_V2_REASON_CODE.noViableProducts,
      //     currentPage,
      //     pageIndex
      //   )
      // }

      // if (isAusIneligible) {
      //   _actions.setLastOfframpReasonCode(SIMPLE_V2_REASON_CODE.dtiUnviable)
      //   _actions.setHasOfframped()
      //   return hardOfframp(
      //     applicationGuid,
      //     SIMPLE_V2_REASON_CODE.dtiUnviable,
      //     currentPage,
      //     pageIndex
      //   )
      // }

      return { outcome: 'continue' }
    }
  ),
})

export default offramp
